// A usage limit (today's allowance, this month's budget, or "too many requests")
// is shown as a modal, not a toast: the student needs to read it, and it says
// what happens next. Any screen can raise one with announceUsageLimit; the
// single UsageLimitModal mounted in App listens for it.

export type UsageLimitKind = "daily" | "monthly" | "rate";

export interface UsageLimitDetail {
  kind: UsageLimitKind;
  /** Server-supplied wording; replaced by the default copy when absent. */
  message?: string;
  /** ISO time the allowance comes back (daily). */
  resetsAt?: string;
}

export const USAGE_LIMIT_EVENT = "novari:usage-limit";

export interface UsageLimitCopy {
  title: string;
  body: string;
}

const COPY: Record<"adult" | "kids", Record<UsageLimitKind, UsageLimitCopy>> = {
  adult: {
    daily: {
      title: "You've used today's AI allowance",
      body: "It resets at midnight UTC. Upgrade for a bigger daily allowance.",
    },
    monthly: {
      title: "This month's AI budget is used up",
      body: "It resets on the first day of next month.",
    },
    rate: {
      title: "That's a lot of requests at once",
      body: "Give it a few seconds, then try again.",
    },
  },
  kids: {
    daily: {
      title: "That's all your questions for today!",
      body: "Come back tomorrow, or ask a grown-up about getting more.",
    },
    monthly: {
      title: "We've used up this month's questions",
      body: "A grown-up can help with that. See you next month!",
    },
    rate: {
      title: "Whoa, that's fast!",
      body: "Take a breath and try again in a moment.",
    },
  },
};

export function usageLimitCopy(
  detail: UsageLimitDetail,
  audience: "adult" | "kids",
): UsageLimitCopy {
  const base = COPY[audience][detail.kind];
  // A kid should never see server wording written for adults (plan names,
  // upgrade prompts), so the friendly copy always wins for kids.
  const message = audience === "adult" ? detail.message?.trim() : undefined;
  return { title: base.title, body: message || base.body };
}

/** Formats a reset time as a local clock time, or null when unusable. */
export function formatResetTime(resetsAt: string | undefined, locale?: string): string | null {
  if (!resetsAt) return null;
  const date = new Date(resetsAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}

export function announceUsageLimit(detail: UsageLimitDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<UsageLimitDetail>(USAGE_LIMIT_EVENT, { detail }));
}
